import { AnimatePresence, motion } from "framer-motion";
import { find, get, isEqual, isObject, reject } from "lodash";
import {
    MultiSelectChangeEvent,
    MultiSelectRemoveEvent,
    MultiSelect as PrimeMultiSelect,
    MultiSelectProps as PrimeMultiSelectProps,
} from "primereact/multiselect";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { mergePt } from "../../utils";
import { ChipTemplate } from "./templates";

export type MultiSelectValue = Record<string, unknown> | string | number;

const MultiSelect = (props: PrimeMultiSelectProps) => {
    const {
        display,
        emptyFilterMessage,
        emptyMessage,
        optionLabel,
        optionValue,
        options,
        value,
        pt,
        onChange,
        onRemove,
        selectedItemTemplate,
        placeholder,
        filterInputAutoFocus = false,
        ...rest
    } = props;

    const { t } = useTranslation("common");

    const isChipMode = display === "chip";
    const selectedValues: MultiSelectValue[] = Array.isArray(value)
        ? value
        : [];
    const optionList: MultiSelectValue[] = Array.isArray(options)
        ? (options as MultiSelectValue[])
        : [];

    const getChipLabel = (item: MultiSelectValue): string => {
        const option = isObject(item)
            ? item
            : find(optionList, (o) => !!optionValue && get(o, optionValue) === item);
        const label = optionLabel ? get(option, optionLabel) : undefined;
        return String(label ?? item);
    };

    const handleRemoveChip = (item: MultiSelectValue) => {
        const newValues = reject(selectedValues, (selectedValue) =>
            isEqual(selectedValue, item)
        );
        onChange?.({
            value: newValues,
            target: { value: newValues },
        } as MultiSelectChangeEvent);
        onRemove?.({ value: item } as MultiSelectRemoveEvent);
    };

    return (
        <PrimeMultiSelect
            display={isChipMode ? undefined : display}
            value={value}
            optionLabel={optionLabel}
            optionValue={optionValue}
            options={options}
            placeholder={placeholder}
            onChange={onChange}
            pt={mergePt(
                {
                    panel: { className: "max-w-0" },
                    wrapper: { className: "overflow-x-hidden!" },
                    item: { className: "[&>span]:min-w-0 [&>span]:truncate" },
                    filterContainer: {
                        className:
                            "[&>input]:block [&>input]:rounded-[6px] [&>input]:px-9 [&>input]:h-9",
                    },
                    filterIcon: {
                        className: "hidden",
                    },
                    closebutton: { className: "hidden" },
                    ...(isChipMode && {
                        label: { className: "pr-0!" },
                    }),
                },
                pt
            )}
            selectedItemTemplate={
                selectedItemTemplate ??
                (isChipMode
                    ? (value) =>
                          value == null ? (
                              <span className="text-surface-700">
                                  {placeholder}
                              </span>
                          ) : (
                              <ChipTemplate
                                  value={value}
                                  selectedValues={selectedValues}
                                  getLabel={getChipLabel}
                                  onRemove={handleRemoveChip}
                              />
                          )
                    : undefined)
            }
            filterInputAutoFocus={filterInputAutoFocus}
            filterTemplate={(filterEvent) => {
                const { filterOptions, props: filterProps } = filterEvent;
                const { filterValue } = filterProps;
                // @ts-ignore PrimeReact typing bug: `element` is a ReactNode at runtime
                const filterElement = filterEvent.element as ReactNode;
                return (
                    <div className="relative w-full">
                        <AnimatePresence>
                            {filterValue && (
                                <motion.button
                                    key="clear-icon"
                                    onMouseDown={(event) =>
                                        event.preventDefault()
                                    }
                                    className="group absolute right-0 top-1/2 -translate-y-1/2 z-10 size-9 p-3 flex items-center justify-center cursor-pointer text-text-500/60 hover:text-text-500"
                                    onClick={() => filterOptions?.reset?.()}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <i className="pi pi-times text-current" />
                                </motion.button>
                            )}
                        </AnimatePresence>
                        <i className="pi pi-search absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none text-text-500/60" />
                        {filterElement}
                    </div>
                );
            }}
            emptyFilterMessage={
                emptyFilterMessage ?? t("common:noResultsFound")
            }
            emptyMessage={emptyMessage ?? (t("common:noResultsFound") || "")}
            {...rest}
        />
    );
};

export default MultiSelect;
